export type IvrDtmfStatus = 'converted' | 'optout' | 'mismatch';

export interface IvrDtmfRule {
  id: string;
  digit: string;
  status: IvrDtmfStatus;
  label: string;
}

export interface IvrDtmfMappingValue {
  status: IvrDtmfStatus;
  label: string | null;
}

export interface IvrDtmfMappingInputValue {
  status: IvrDtmfStatus;
  label?: string | null;
}

export const IVR_DTMF_STATUSES: IvrDtmfStatus[] = ['converted', 'optout', 'mismatch'];

export const createIvrRule = (
  overrides?: Partial<Omit<IvrDtmfRule, 'id'>>,
): IvrDtmfRule => ({
  id: `ivr-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  digit: '',
  status: 'mismatch',
  label: '',
  ...overrides,
});

export function parseIvrDtmfRules(
  mapping: Record<string, IvrDtmfMappingInputValue> | null | undefined,
): IvrDtmfRule[] {
  const entries = Object.entries(mapping || {}).sort(([digitA], [digitB]) => digitA.localeCompare(digitB));
  if (entries.length === 0) {
    return [createIvrRule({ digit: '1', status: 'converted' })];
  }
  return entries.map(([digit, value]) => createIvrRule({
    digit,
    status: value.status,
    label: value.label || '',
  }));
}

export function getIvrDtmfValidationErrorKey(
  ivrDtmfRules: IvrDtmfRule[],
): string | null {
  const normalizedRules = ivrDtmfRules
    .map((rule) => ({
      digit: (rule.digit || '').trim(),
      status: rule.status,
      label: (rule.label || '').trim(),
    }))
    .filter((rule) => rule.digit || rule.label || rule.status);

  if (normalizedRules.length === 0) {
    return 'pages:campaigns.create.errors.ivrDtmfRequired';
  }

  const usedDigits = new Set<string>();
  let hasConverted = false;

  for (const rule of normalizedRules) {
    if (!rule.digit) {
      return 'pages:campaigns.create.errors.ivrDtmfRequired';
    }
    if (usedDigits.has(rule.digit)) {
      return 'pages:campaigns.create.errors.ivrDtmfDuplicateDigit';
    }
    usedDigits.add(rule.digit);
    if (rule.status === 'converted') {
      hasConverted = true;
    }
  }

  if (!hasConverted) {
    return 'pages:campaigns.create.errors.ivrDtmfConvertedRequired';
  }

  return null;
}

export function buildIvrDtmfMapPayload(
  ivrDtmfRules: IvrDtmfRule[],
): Record<string, IvrDtmfMappingValue> {
  const payload: Record<string, IvrDtmfMappingValue> = {};
  for (const rule of ivrDtmfRules) {
    const digit = (rule.digit || '').trim();
    if (!digit) {
      continue;
    }
    payload[digit] = {
      status: rule.status,
      label: (rule.label || '').trim() || null,
    };
  }
  return payload;
}
